/* ==========================================================================
   Samaná — Probador virtual de gafas

   Todo pasa dentro del navegador: la foto nunca sale del dispositivo.

   Se busca la cara en la foto y de ahí sale todo: dónde va la gafa, de qué tamaño
   y cuánto inclinada. Si la detección no llega a cargar, se puede acomodar a mano.
   ========================================================================== */
(function () {
  'use strict';

  // Posición de arranque, antes de saber dónde está la cara.
  var OJOS_Y_PCT = 40;
  // Ancho de la gafa respecto del escenario cuando el tamaño está en 100.
  var GAFA_BASE_PCT = 76;

  // Lienzo de la foto: CUADRADO, igual que el escenario. Si no coincidieran, la
  // foto se recortaría al mostrarse y las gafas caerían corridas.
  var LIENZO_W = 1000;
  var LIENZO_H = 1000;

  // ---- Escala real de cada armazón ----
  // Las 8 fotos de armazón se recortaron con la misma escala, así que el ancho en
  // píxeles de cada archivo es proporcional a su ancho REAL en milímetros. Con eso,
  // un modelo más grande se ve más grande sobre la misma cara.
  var ANCHO_REF = 1300; // ancho típico de esas fotos, sirve de referencia
  // Cuántas veces entra la distancia entre pupilas en el ancho del armazón.
  // (distancia entre pupilas ~63 mm, frente de armazón ~145 mm).
  var VECES_IPD = 2.3;
  // Las gafas no quedan centradas en la pupila: el ojo cae en la mitad de ARRIBA del
  // cristal, así que el armazón va unos 7 mm más abajo. Se expresa en proporción de la
  // distancia entre ojos (~63 mm) para que valga igual en cualquier foto y cualquier cara.
  var BAJAR_IPD = 0.12;
  // Cuánto mide, en caras de frente, el tramo ojos→pera comparado con la distancia entre
  // ojos: sirve para darse cuenta de si la cara mira para arriba o para abajo. Medido
  // sobre caras reales (dio 1,82 y 2,08); de cara a cara varía bastante, así que el
  // resultado se recorta corto para que una cara larga o corta no desacomode la gafa.
  var OJOS_BARBILLA = 1.95;

  // Detección de rostro: se descarga solo cuando alguien usa el probador.
  // Se usan los puntos de la cara (no el detector simple) porque incluyen las PUPILAS
  // y la orientación de la cabeza en los tres ejes.
  var MP = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18';
  var MODELO = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
  var detector = null;
  var detectorRoto = false;

  // Puntos que interesan (numeración de la malla facial). Los ojos se ubican por sus
  // DOS esquinas y no por el iris: así la gafa no se mueve si la persona mira de reojo.
  var P = {
    ojoA1: 33, ojoA2: 133,    // esquinas de un ojo
    ojoB1: 362, ojoB2: 263,   // esquinas del otro
    puente: 168,              // nacimiento de la nariz, justo entre los ojos
    barbilla: 152,
  };

  async function obtenerDetector() {
    if (detector || detectorRoto) return detector;
    try {
      var vision = await import(MP + '/vision_bundle.mjs');
      var wasm = await vision.FilesetResolver.forVisionTasks(MP + '/wasm');
      detector = await vision.FaceLandmarker.createFromOptions(wasm, {
        baseOptions: { modelAssetPath: MODELO },
        runningMode: 'IMAGE',
        numFaces: 1, // la cara principal de la foto: la de quien se saca la selfie
      });
    } catch (e) {
      detectorRoto = true; // sin detección: se acomoda a mano
    }
    return detector;
  }

  function cargarImagen(src) {
    return new Promise(function (listo, falla) {
      var img = new Image();
      img.onload = function () { listo(img); };
      img.onerror = falla;
      img.src = src;
    });
  }

  // Dónde y cómo colocar las gafas sobre una foto. Devuelve null si no hay cara.
  //
  // Nadie se saca la selfie perfectamente derecho, así que se contemplan las tres
  // formas de tener la cabeza torcida:
  //   · ladeada (una oreja hacia el hombro) → se mide por el ángulo entre los ojos
  //     y la gafa se gira lo mismo.
  //   · girada (la pera apuntando a un costado) → la cara pierde simetría: el puente
  //     de la nariz deja de estar en el medio de los dos ojos. Por eso la gafa se
  //     centra en el PUENTE y no en el punto medio entre ojos.
  //   · para arriba o para abajo → la cara se acorta a lo alto. Se detecta comparando
  //     el tramo ojos→pera contra la distancia entre ojos, y se achica en la misma
  //     proporción lo que la gafa baja respecto de los ojos.
  async function medirRostro(dataURL) {
    var det = await obtenerDetector();
    if (!det) return null;
    try {
      var img = await cargarImagen(dataURL);
      var r = det.detect(img);
      var puntos = r && r.faceLandmarks && r.faceLandmarks[0];
      if (!puntos || puntos.length < 468) return null;

      var W = img.naturalWidth, H = img.naturalHeight;
      var px = function (i) { return { x: puntos[i].x * W, y: puntos[i].y * H }; };
      var medio = function (a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; };

      // Centro de cada ojo (promedio de sus dos esquinas) y puente de la nariz.
      var ojo1 = medio(px(P.ojoA1), px(P.ojoA2));
      var ojo2 = medio(px(P.ojoB1), px(P.ojoB2));
      var puente = px(P.puente);
      var barbilla = px(P.barbilla);

      // Cuál cae a la izquierda en la foto no depende de la numeración: la foto puede
      // venir espejada (selfie). Se ordenan por posición para que el ángulo sea correcto.
      var ojoIzq = ojo1.x <= ojo2.x ? ojo1 : ojo2;
      var ojoDer = ojo1.x <= ojo2.x ? ojo2 : ojo1;

      var dx = ojoDer.x - ojoIzq.x;
      var dy = ojoDer.y - ojoIzq.y;
      var ipd = Math.sqrt(dx * dx + dy * dy);
      if (!ipd) return null;

      // Ejes de la CARA (no de la foto): uno sigue la línea de los ojos y el otro
      // apunta hacia el mentón. Así todo lo que sigue respeta la cabeza ladeada.
      var eje = { x: dx / ipd, y: dy / ipd };
      var abajo = { x: -eje.y, y: eje.x };

      var entreOjos = medio(ojoIzq, ojoDer);

      // Simetría: se corre el centro hasta el puente, pero solo a lo ancho de la cara.
      // Con la cara de frente el puente ya está en el medio y esto no cambia nada;
      // con la cara girada, corrige lo que la nariz se desplazó.
      var corrimiento = (puente.x - entreOjos.x) * eje.x + (puente.y - entreOjos.y) * eje.y;
      var anclaX = entreOjos.x + eje.x * corrimiento;
      var anclaY = entreOjos.y + eje.y * corrimiento;

      // Cara mirando arriba/abajo: el tramo ojos→pera se acorta o se estira.
      var alto = (barbilla.x - entreOjos.x) * abajo.x + (barbilla.y - entreOjos.y) * abajo.y;
      var escorzo = alto / (ipd * OJOS_BARBILLA);
      if (!isFinite(escorzo)) escorzo = 1;
      escorzo = Math.max(0.8, Math.min(1.2, escorzo));

      // Y por último se baja hasta donde apoyan de verdad las gafas.
      var bajar = ipd * BAJAR_IPD * escorzo;

      return {
        cxPct: ((anclaX + abajo.x * bajar) / W) * 100,
        cyPct: ((anclaY + abajo.y * bajar) / H) * 100,
        ipdRel: ipd / W,  // distancia entre ojos, en proporción del ancho de la foto
        angulo: Math.atan2(dy, dx) * 180 / Math.PI,
      };
    } catch (e) {
      return null;
    }
  }

  function iniciar(raiz) {
    if (!raiz || raiz.__prListo) return;
    raiz.__prListo = true;

    var datos = raiz.querySelector('[data-pr-modelos]');
    var modelos = [];
    if (datos) { try { modelos = JSON.parse(datos.textContent); } catch (e) { modelos = []; } }
    if (!modelos.length) return;

    var escenario = raiz.querySelector('[data-pr-escenario]');
    var video = raiz.querySelector('[data-pr-video]');
    var foto = raiz.querySelector('[data-pr-foto]');
    var conjunto = raiz.querySelector('[data-pr-set]');
    var gafa = raiz.querySelector('[data-pr-gafa]');
    var lente = raiz.querySelector('[data-pr-lente]');
    var lenteImg = raiz.querySelector('[data-pr-lente-img]');
    var falta = raiz.querySelector('[data-pr-falta]');
    var ajustes = raiz.querySelector('[data-pr-ajustes]');
    var aviso = raiz.querySelector('[data-pr-aviso]');
    var avisoTxt = raiz.querySelector('[data-pr-aviso-txt]');
    var sliderTamano = raiz.querySelector('[data-pr-tamano]');
    var sliderAlto = raiz.querySelector('[data-pr-alto]');
    var inputArchivo = raiz.querySelector('[data-pr-archivo]');
    var btnComprar = raiz.querySelector('[data-pr-comprar]');
    var elegidoTxt = raiz.querySelector('[data-pr-elegido-txt]');
    var elegidoPrecio = raiz.querySelector('[data-pr-elegido-precio]');

    var chipsFiltro = Array.prototype.slice.call(raiz.querySelectorAll('[data-pr-filtro]'));
    var chipsMarco = Array.prototype.slice.call(raiz.querySelectorAll('[data-pr-marco]'));

    // Los marcos se muestran en el MISMO orden que en la ficha del producto. Ese orden
    // lo define el editor (bloques de la sección de producto), así que se lee del propio
    // HTML de la página en vez de mantener una segunda lista acá.
    (function ordenarComoEnProducto() {
      var enFicha = document.querySelectorAll('[data-pg-marco]');
      if (!enFicha.length || !chipsMarco.length) return;
      var orden = [];
      Array.prototype.forEach.call(enFicha, function (b) {
        var n = b.getAttribute('data-pg-marco');
        if (n && orden.indexOf(n) === -1) orden.push(n);
      });
      if (!orden.length) return;
      var lugar = function (c) {
        var i = orden.indexOf(c.dataset.prMarco);
        return i === -1 ? 999 : i;
      };
      chipsMarco.sort(function (a, b) { return lugar(a) - lugar(b); });
      var caja = chipsMarco[0].parentNode;
      chipsMarco.forEach(function (c) { caja.appendChild(c); });
    })();

    var estado = {
      filtro: modelos[0].filtro,
      marco: modelos[0].marco,
      fotoLista: null,      // dataURL ya recortada a 3:4
      anchoBase: GAFA_BASE_PCT, // ancho "100%": de la guía o del rostro detectado
      anchoPct: GAFA_BASE_PCT,
      cxPct: 50,
      cyPct: OJOS_Y_PCT,
      rotDeg: 0,
      rostro: null,         // medidas de los ojos, si se detectó la cara
    };

    var stream = null;

    // ---------- Pantallas ----------

    function mostrar(cual) {
      Array.prototype.forEach.call(raiz.querySelectorAll('[data-pr-pantalla]'), function (p) {
        p.hidden = p.getAttribute('data-pr-pantalla') !== cual;
      });
      if (aviso) aviso.hidden = true;
    }

    function mostrarAviso(texto) {
      if (!aviso || !avisoTxt) return;
      avisoTxt.textContent = texto;
      aviso.hidden = false;
      apagarCamara();
    }

    // ---------- Cámara ----------

    function apagarCamara() {
      if (!stream) return;
      stream.getTracks().forEach(function (t) { try { t.stop(); } catch (e) { /* ya parada */ } });
      stream = null;
    }

    async function encenderCamara() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        mostrarAviso('Tu navegador no permite usar la cámara desde la web. Podés subir una foto en su lugar.');
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } },
          audio: false,
        });
        video.srcObject = stream;
        await video.play().catch(function () { /* algunos navegadores lo hacen solos */ });
        mostrar('camara');
      } catch (e) {
        if (e && (e.name === 'NotAllowedError' || e.name === 'SecurityError')) {
          mostrarAviso('No nos diste permiso para usar la cámara. Podés habilitarlo desde el candado de la barra de direcciones, o subir una foto.');
        } else if (e && e.name === 'NotFoundError') {
          mostrarAviso('No encontramos una cámara en este dispositivo. Probá subiendo una foto.');
        } else {
          mostrarAviso('No pudimos abrir la cámara. Probá subiendo una foto.');
        }
      }
    }

    // ---------- Normalizar la foto a 3:4 ----------
    // Se recorta como "cover" (igual que se ve en pantalla) y, si viene de la
    // cámara frontal, se refleja: así la imagen guardada es idéntica a la que vio.

    function normalizar(fuente, anchoNat, altoNat, espejar) {
      var lienzo = document.createElement('canvas');
      lienzo.width = LIENZO_W;
      lienzo.height = LIENZO_H;
      var ctx = lienzo.getContext('2d');

      var escala = Math.max(LIENZO_W / anchoNat, LIENZO_H / altoNat);
      var w = anchoNat * escala;
      var h = altoNat * escala;
      var x = (LIENZO_W - w) / 2;
      var y = (LIENZO_H - h) / 2;

      if (espejar) {
        ctx.translate(LIENZO_W, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(fuente, x, y, w, h);
      return lienzo.toDataURL('image/jpeg', 0.92);
    }

    // Deja lista la foto y, si se puede, acomoda las gafas al rostro.
    //
    // Las gafas se mantienen OCULTAS mientras se busca la cara: si se mostraran antes,
    // aparecerían un instante en un tamaño que no corresponde y saltarían al acomodarse.
    async function usarFoto(dataURL) {
      estado.fotoLista = dataURL;
      estado.rostro = null;
      reset();
      var auto = raiz.dataset.prAuto !== 'no';
      if (auto) taparGafas(true);
      pintar();
      mostrar('resultado');
      if (!auto) return;
      marcarBuscando(true);
      var medida = await medirRostro(dataURL);
      marcarBuscando(false);
      if (medida) {
        estado.rostro = medida;
        ajustarARostro();
      }
      taparGafas(false); // recién ahora se muestran, ya en su sitio
    }

    function taparGafas(si) {
      [conjunto, lente].forEach(function (capa) {
        if (capa) capa.classList.toggle('is-esperando', !!si);
      });
    }

    function marcarBuscando(si) {
      if (!escenario) return;
      escenario.classList.toggle('is-buscando', !!si);
    }

    function disparar() {
      if (!video.videoWidth) return;
      var foto = normalizar(video, video.videoWidth, video.videoHeight, true);
      apagarCamara();
      usarFoto(foto);
    }

    function usarArchivo(archivo) {
      if (!archivo) return;
      var lector = new FileReader();
      lector.onload = function () {
        var img = new Image();
        img.onload = function () {
          usarFoto(normalizar(img, img.naturalWidth, img.naturalHeight, false));
        };
        img.onerror = function () { mostrarAviso('No pudimos leer esa imagen. Probá con otra.'); };
        img.src = lector.result;
      };
      lector.onerror = function () { mostrarAviso('No pudimos leer el archivo. Probá con otra imagen.'); };
      lector.readAsDataURL(archivo);
    }

    // ---------- Modelos ----------

    function buscarModelo(filtro, marco) {
      return modelos.filter(function (m) { return m.filtro === filtro && m.marco === marco; })[0];
    }

    function marcosDe(filtro) {
      return modelos.filter(function (m) { return m.filtro === filtro; });
    }

    function reset() {
      estado.anchoBase = GAFA_BASE_PCT;
      estado.anchoPct = GAFA_BASE_PCT;
      estado.cxPct = 50;
      estado.cyPct = OJOS_Y_PCT;
      estado.rotDeg = 0;
      if (sliderTamano) sliderTamano.value = 100;
      if (sliderAlto) sliderAlto.value = 0;
    }

    // Con la cara medida, cada armazón se coloca en su tamaño real: el ancho sale de
    // la distancia entre pupilas y del tamaño propio de ese modelo.
    function ajustarARostro() {
      if (!estado.rostro) return;
      var r = estado.rostro;
      var propio = (gafa && gafa.naturalWidth) ? gafa.naturalWidth / ANCHO_REF : 1;
      estado.anchoBase = r.ipdRel * VECES_IPD * propio * 100;
      estado.anchoPct = estado.anchoBase * (sliderTamano ? Number(sliderTamano.value) / 100 : 1);
      // El punto ya viene calculado sobre la cara: centrado en el puente de la nariz
      // y a la altura donde apoyan las gafas.
      estado.cxPct = r.cxPct;
      estado.cyPct = r.cyPct + (sliderAlto ? Number(sliderAlto.value) * 0.25 : 0);
      estado.rotDeg = r.angulo;
      colocar();
    }

    // ---------- Pintar ----------

    // El armazón y el cristal son dos capas sueltas que tienen que viajar juntas:
    // se les aplica siempre la misma posición y el mismo ancho.
    function colocar() {
      [conjunto, lente].forEach(function (capa) {
        if (!capa) return;
        capa.style.width = estado.anchoPct + '%';
        capa.style.left = estado.cxPct + '%';
        capa.style.top = estado.cyPct + '%';
        capa.style.setProperty('--pr-rot', estado.rotDeg + 'deg');
      });
    }

    function pintar() {
      var m = buscarModelo(estado.filtro, estado.marco);
      if (!m) {
        var alt = marcosDe(estado.filtro)[0];
        if (!alt) return;
        estado.marco = alt.marco;
        m = alt;
      }

      chipsFiltro.forEach(function (c) {
        c.setAttribute('aria-checked', c.dataset.prFiltro === estado.filtro ? 'true' : 'false');
      });
      chipsMarco.forEach(function (c) {
        var esDeEste = c.dataset.prDeFiltro === estado.filtro;
        c.hidden = !esDeEste;
        c.setAttribute('aria-checked', esDeEste && c.dataset.prMarco === estado.marco ? 'true' : 'false');
      });

      if (foto && estado.fotoLista) foto.src = estado.fotoLista;

      var hayFoto = !!m.foto;
      if (gafa) {
        gafa.hidden = !hayFoto;
        // Cada armazón tiene su propio ancho real: al cambiar de modelo hay que
        // recalcular la escala en cuanto se conocen sus medidas.
        if (hayFoto && gafa.getAttribute('src') !== m.foto) {
          gafa.onload = function () { ajustarARostro(); };
          gafa.src = m.foto;
        }
      }
      // El cristal: mismo recorte que el armazón, pintado del color del filtro.
      if (lente) {
        var hayLente = hayFoto && !!m.lente;
        lente.hidden = !hayLente;
        if (hayLente) {
          lente.style.setProperty('--pr-tinte', m.tinte || '#E4883A');
          lente.style.setProperty('--pr-lente', 'url("' + m.lente + '")');
          if (lenteImg && lenteImg.getAttribute('src') !== m.lente) lenteImg.src = m.lente;
        }
      }
      if (conjunto) conjunto.hidden = !hayFoto;
      colocar();
      if (falta) falta.hidden = hayFoto;
      if (ajustes) ajustes.hidden = !estado.fotoLista;

      // Resumen y botón de compra del modelo elegido
      if (elegidoTxt) elegidoTxt.textContent = estado.filtro + ' · ' + estado.marco;
      if (elegidoPrecio) elegidoPrecio.textContent = m.precio || '';
      if (btnComprar) {
        btnComprar.disabled = !m.disponible;
        btnComprar.textContent = m.disponible
          ? (btnComprar.dataset.label || 'Elijo este modelo')
          : (btnComprar.dataset.labelAgotado || 'Sin stock');
      }
    }

    // ---------- Arrastrar la gafa ----------

    if (conjunto) {
      var arrastrando = false;
      var desde = null;

      conjunto.addEventListener('pointerdown', function (e) {
        if (!estado.fotoLista) return;
        arrastrando = true;
        conjunto.classList.add('is-arrastrando');
        desde = { x: e.clientX, y: e.clientY, cx: estado.cxPct, cy: estado.cyPct };
        try { conjunto.setPointerCapture(e.pointerId); } catch (err) { /* no siempre está */ }
      });

      conjunto.addEventListener('pointermove', function (e) {
        if (!arrastrando || !desde) return;
        var caja = escenario.getBoundingClientRect();
        estado.cxPct = Math.min(95, Math.max(5, desde.cx + ((e.clientX - desde.x) / caja.width) * 100));
        estado.cyPct = Math.min(95, Math.max(5, desde.cy + ((e.clientY - desde.y) / caja.height) * 100));
        colocar();
      });

      function soltar() {
        arrastrando = false;
        desde = null;
        conjunto.classList.remove('is-arrastrando');
      }
      conjunto.addEventListener('pointerup', soltar);
      conjunto.addEventListener('pointercancel', soltar);
    }

    // ---------- Controles ----------

    chipsFiltro.forEach(function (c) {
      c.addEventListener('click', function () {
        if (c.dataset.prFiltro === estado.filtro) return;
        estado.filtro = c.dataset.prFiltro;
        var deEste = marcosDe(estado.filtro);
        if (!deEste.filter(function (m) { return m.marco === estado.marco; }).length && deEste.length) {
          estado.marco = deEste[0].marco;
        }
        pintar();
      });
    });

    chipsMarco.forEach(function (c) {
      c.addEventListener('click', function () {
        estado.marco = c.dataset.prMarco;
        pintar();
      });
    });

    // Los deslizadores ajustan SOBRE la medida base (la del rostro si se detectó).
    if (sliderTamano) {
      sliderTamano.addEventListener('input', function () {
        estado.anchoPct = estado.anchoBase * (Number(sliderTamano.value) / 100);
        colocar();
      });
    }

    if (sliderAlto) {
      sliderAlto.addEventListener('input', function () {
        var base = estado.rostro ? estado.rostro.cyPct : OJOS_Y_PCT;
        estado.cyPct = base + Number(sliderAlto.value) * 0.25;
        colocar();
      });
    }

    var btnCamara = raiz.querySelector('[data-pr-camara]');
    if (btnCamara) btnCamara.addEventListener('click', encenderCamara);

    if (inputArchivo) {
      inputArchivo.addEventListener('change', function () {
        usarArchivo(inputArchivo.files && inputArchivo.files[0]);
        inputArchivo.value = ''; // permite volver a elegir la misma foto
      });
    }

    var btnDisparar = raiz.querySelector('[data-pr-disparar]');
    if (btnDisparar) btnDisparar.addEventListener('click', disparar);

    var btnCancelar = raiz.querySelector('[data-pr-cancelar]');
    if (btnCancelar) btnCancelar.addEventListener('click', function () { apagarCamara(); mostrar('inicio'); });

    Array.prototype.forEach.call(raiz.querySelectorAll('[data-pr-reiniciar]'), function (b) {
      b.addEventListener('click', function () {
        apagarCamara();
        estado.fotoLista = null;
        estado.rostro = null;
        reset();
        pintar();
        mostrar('inicio');
      });
    });

    // ---------- Guardar la imagen ----------

    var btnGuardar = raiz.querySelector('[data-pr-guardar]');
    if (btnGuardar) {
      btnGuardar.addEventListener('click', function () {
        if (!estado.fotoLista) return;

        var lienzo = document.createElement('canvas');
        lienzo.width = LIENZO_W;
        lienzo.height = LIENZO_H;
        var ctx = lienzo.getContext('2d');

        var base = new Image();
        base.onload = function () {
          ctx.drawImage(base, 0, 0, LIENZO_W, LIENZO_H);

          function bajar() {
            lienzo.toBlob(function (blob) {
              if (!blob) return;
              var url = URL.createObjectURL(blob);
              var a = document.createElement('a');
              a.href = url;
              a.download = 'samana-' + estado.filtro + '-' + estado.marco + '.png';
              document.body.appendChild(a);
              a.click();
              a.remove();
              setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
            }, 'image/png');
          }

          // Si el modelo tiene foto, se dibuja encima en la misma posición que en pantalla.
          if (!gafa || gafa.hidden || !gafa.getAttribute('src')) { bajar(); return; }

          var m = buscarModelo(estado.filtro, estado.marco) || {};
          var w = LIENZO_W * (estado.anchoPct / 100);
          var x0 = LIENZO_W * (estado.cxPct / 100);
          var y0 = LIENZO_H * (estado.cyPct / 100);

          // Primero el cristal (teñido, multiplicando sobre la cara), después el armazón:
          // el mismo orden que en pantalla.
          // Se dibuja girado lo mismo que en pantalla (si el rostro venía inclinado).
          function conGiro(pintarEncima) {
            ctx.save();
            ctx.translate(x0, y0);
            ctx.rotate((estado.rotDeg || 0) * Math.PI / 180);
            pintarEncima();
            ctx.restore();
          }

          function dibujarArmazon() {
            var g = new Image();
            g.crossOrigin = 'anonymous'; // las fotos del tema vienen del CDN de Shopify
            g.onload = function () {
              var h = w * (g.naturalHeight / g.naturalWidth);
              conGiro(function () { ctx.drawImage(g, -w / 2, -h / 2, w, h); });
              bajar();
            };
            g.onerror = function () { bajar(); }; // sin la gafa, pero al menos baja la foto
            g.src = gafa.getAttribute('src');
          }

          if (!m.lente || !lente || lente.hidden) { dibujarArmazon(); return; }

          var mask = new Image();
          mask.crossOrigin = 'anonymous';
          mask.onload = function () {
            var h = w * (mask.naturalHeight / mask.naturalWidth);
            // Se tiñe la máscara en un lienzo aparte: se pinta el color solo donde
            // la máscara tiene cristal (source-in).
            var aux = document.createElement('canvas');
            aux.width = Math.max(1, Math.round(w));
            aux.height = Math.max(1, Math.round(h));
            var actx = aux.getContext('2d');
            actx.drawImage(mask, 0, 0, aux.width, aux.height);
            actx.globalCompositeOperation = 'source-in';
            actx.fillStyle = m.tinte || '#E4883A';
            actx.fillRect(0, 0, aux.width, aux.height);

            var opacidad = 0.7;
            try { opacidad = parseFloat(getComputedStyle(lente).opacity) || 0.7; } catch (e) { /* valor por defecto */ }
            ctx.save();
            ctx.globalCompositeOperation = 'multiply';
            ctx.globalAlpha = opacidad;
            conGiro(function () { ctx.drawImage(aux, -w / 2, -h / 2, w, h); });
            ctx.restore();
            dibujarArmazon();
          };
          mask.onerror = dibujarArmazon;
          mask.src = m.lente;
        };
        base.src = estado.fotoLista;
      });
    }

    // ---------- Elijo este modelo ----------
    // Agrega al carrito la variante de la combinación elegida y lleva al carrito,
    // para que vea el resumen del pedido y pueda pagar.

    if (btnComprar) {
      btnComprar.addEventListener('click', async function () {
        var m = buscarModelo(estado.filtro, estado.marco);
        if (!m || !m.disponible || btnComprar.disabled) return;

        var textoOriginal = btnComprar.textContent;
        btnComprar.disabled = true;
        btnComprar.textContent = btnComprar.dataset.labelCargando || 'Agregando…';

        try {
          var r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: m.id, quantity: 1 }] }),
          });
          if (!r.ok) throw new Error('no se pudo agregar');
          apagarCamara();
          window.location.href = '/cart';
        } catch (e) {
          // Si algo falla, se cae al camino clásico: el enlace directo de Shopify.
          window.location.href = '/cart/' + m.id + ':1';
        } finally {
          btnComprar.disabled = false;
          btnComprar.textContent = textoOriginal;
        }
      });
    }

    // Si la persona se va de la página, apagar la cámara.
    window.addEventListener('pagehide', apagarCamara);

    pintar();
    mostrar('inicio');
  }

  function arrancar() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sm-probador]'), iniciar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var raiz = e.target.querySelector('[data-sm-probador]');
    if (raiz) iniciar(raiz);
  });
})();
